import React, { useEffect, useState } from "react";

import { ChevronRight, BarChart3, Loader2 } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../components/ui/collapsible";
import { Card, CardContent } from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { cloudUsageApi, UsageSummary, formatBytes, formatNumber } from "../../../lib/api/cloud-usage";
import { cn } from "../../../lib/utils";

interface CloudUsageCardProps {
  className?: string;
}

interface ProjectBreakdown {
  projectName: string;
  value: string;
  isDeleted?: boolean;
}

interface UsageRowProps {
  label: string;
  total: string;
  projects?: ProjectBreakdown[];
}

const UsageRow: React.FC<UsageRowProps> = ({ label, total, projects }) => {
  const [open, setOpen] = useState(false);
  const hasProjects = projects && projects.length > 0;

  if (!hasProjects) {
    return (
      <div className="flex items-center justify-between py-2 px-1">
        <span className="text-sm">{label}</span>
        <span className="text-sm font-medium tabular-nums">{total}</span>
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2 px-1 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
        <div className="flex items-center gap-1.5">
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
              open && "rotate-90"
            )}
          />
          <span className="text-sm">{label}</span>
        </div>
        <span className="text-sm font-medium tabular-nums">{total}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-5 py-1 divide-y divide-border/50">
          {projects.map((project) => (
            <div key={project.projectName} className="flex items-center justify-between py-1.5 pl-2">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                {project.projectName}
                {project.isDeleted && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded-full text-muted-foreground/70">
                    Deleted
                  </span>
                )}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{project.value}</span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const CloudUsageCard: React.FC<CloudUsageCardProps> = ({ className }) => {
  const { getIdToken } = useAuth();
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsage();
  }, []);

  const loadUsage = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required");
        return;
      }
      const data = await cloudUsageApi.getSummary(token);
      setUsage(data);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      console.error("Error loading usage:", err);
      if (axiosErr.response?.status !== 404) {
        setError(axiosErr.response?.data?.message || "Failed to load usage data");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (start: string, _end: string): string => {
    const startDate = new Date(start);
    const options: Intl.DateTimeFormatOptions = { month: "short", year: "numeric", timeZone: "UTC" };
    return startDate.toLocaleDateString("en-US", options);
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const hasSupabaseUsage = usage && (
    usage.supabase.edgeFunctionInvocations > 0 ||
    (usage.supabase.realtimeMessages ?? 0) > 0 ||
    (usage.supabase.realtimePeakConnections ?? 0) > 0
  );
  const hasCloudflareUsage = usage?.cloudflare && (
    usage.cloudflare.gatewayRequests > 0 ||
    usage.cloudflare.contentDeliveryRequests > 0
  );

  if (!usage || (!hasSupabaseUsage && !hasCloudflareUsage)) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
            <BarChart3 className="h-4 w-4" />
            <span>Usage by Project</span>
          </div>
          <p className="text-sm text-muted-foreground">
            No usage data available yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  const byProject = usage.byProject ?? [];

  const edgeFunctionProjects: ProjectBreakdown[] = byProject
    .filter((p) => p.edgeFunctionInvocations > 0)
    .map((p) => ({ projectName: p.projectName, value: formatNumber(p.edgeFunctionInvocations), isDeleted: p.isDeleted }));

  const realtimeMessageProjects: ProjectBreakdown[] = byProject
    .filter((p) => (p.realtimeMessages ?? 0) > 0)
    .map((p) => ({
      projectName: p.projectName,
      value: formatNumber(p.realtimeMessages ?? 0),
      isDeleted: p.isDeleted,
    }));

  const realtimeConnectionProjects: ProjectBreakdown[] = byProject
    .filter((p) => (p.realtimePeakConnections ?? 0) > 0)
    .map((p) => ({
      projectName: p.projectName,
      value: formatNumber(p.realtimePeakConnections ?? 0),
      isDeleted: p.isDeleted,
    }));

  const appTrafficProjects: ProjectBreakdown[] = byProject
    .filter((p) => p.gatewayRequests + p.contentDeliveryRequests > 0)
    .map((p) => ({
      projectName: p.projectName,
      value: formatNumber(p.gatewayRequests + p.contentDeliveryRequests),
      isDeleted: p.isDeleted,
    }));

  const totalAppTraffic =
    (usage.cloudflare?.gatewayRequests ?? 0) + (usage.cloudflare?.contentDeliveryRequests ?? 0);

  return (
    <Card className={className}>
      <CardContent className="py-4 space-y-4">
        {/* Period indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <BarChart3 className="h-4 w-4" />
            <span>Usage by Project</span>
          </div>
          <span className="text-sm text-muted-foreground">
            {formatPeriod(usage.period.start, usage.period.end)}
          </span>
        </div>

        {/* Usage rows */}
        <div className="space-y-0.5">
          <UsageRow
            label="Edge Functions"
            total={formatNumber(usage.supabase.edgeFunctionInvocations)}
            projects={edgeFunctionProjects}
          />
          <UsageRow
            label="Realtime Messages"
            total={formatNumber(usage.supabase.realtimeMessages ?? 0)}
            projects={realtimeMessageProjects}
          />
          <UsageRow
            label="Realtime Connections"
            total={formatNumber(usage.supabase.realtimePeakConnections ?? 0)}
            projects={realtimeConnectionProjects}
          />
          <UsageRow
            label="App Traffic"
            total={formatNumber(totalAppTraffic)}
            projects={appTrafficProjects}
          />
        </div>
      </CardContent>
    </Card>
  );
};
